import type { ClinicSettings, FollowUp, MessageKind, Patient, TemplateKey } from '@/lib/clinic/types'

import { formatDateLong, formatTime } from '@/lib/clinic/dates'
import { toWhatsAppNumber } from '@/lib/clinic/format'

export interface MessageValues {
    patientName: string
    /** YYYY-MM-DD */
    date: string | null
    /** HH:mm */
    time: string | null
    treatment: string
    clinicName: string
    clinicPhone: string
    doctorName: string
}

/**
 * Fills [Placeholders] in a message template. When no time is known,
 * phrases like "at [Time]" are dropped so the sentence still reads well.
 */
export const fillTemplate = (template: string, values: MessageValues): string => {
    let text = template
    const time = formatTime(values.time)
    const date = formatDateLong(values.date)

    if (!time) text = text.replace(/\s*(?:at|by)\s*\[Time\]/gi, '').replace(/\s*\[Time\]/gi, '')
    if (!date) text = text.replace(/\s*(?:on|for)\s*\[Date\]/gi, ' soon').replace(/\s*\[Date\]/gi, '')

    const replacements: Record<string, string> = {
        '[patient name]': values.patientName,
        '[date]': date,
        '[time]': time,
        '[treatment]': values.treatment || 'your dental treatment',
        '[clinic name]': values.clinicName,
        '[clinic phone]': values.clinicPhone,
        '[doctor name]': values.doctorName
    }

    return text.replace(/\[[^\]]+\]/g, match => replacements[match.toLowerCase()] ?? match).replace(/\s{2,}/g, ' ').trim()
}

export const buildMessageValues = (patient: Pick<Patient, 'name'>, followUp: Pick<FollowUp, 'dueDate' | 'dueTime' | 'reason'> | null, settings: ClinicSettings): MessageValues => ({
    patientName: patient.name,
    date: followUp?.dueDate ?? null,
    time: followUp?.dueTime ?? null,
    treatment: followUp?.reason ?? '',
    clinicName: settings.clinic.name,
    clinicPhone: settings.followUp.contactPhone || settings.clinic.phone,
    doctorName: settings.clinic.doctorName
})

export const composeMessage = (kind: MessageKind, values: MessageValues, settings: ClinicSettings): string => {
    if (kind === 'custom') return `Hello ${values.patientName}, this is ${values.clinicName}. `

    return fillTemplate(settings.followUp.templates[kind], values)
}

export const suggestTemplate = (followUp: Pick<FollowUp, 'dueDate' | 'status' | 'confirmedAt'> | null, today: string, settings: ClinicSettings): MessageKind => {
    if (!followUp || followUp.status !== 'pending') return 'custom'
    if (followUp.dueDate < today) return settings.followUp.overdueTemplate

    return settings.followUp.upcomingTemplate as TemplateKey
}

export const buildWhatsAppLink = (phone: string, message: string, countryCode: string): string => {
    const number = toWhatsAppNumber(phone, countryCode)
    const text = message.trim() ? `?text=${encodeURIComponent(message.trim())}` : ''

    return `https://wa.me/${number}${text}`
}

export interface SendResult {
    /**
     * `opened`: a chat was opened for a person to press send (click-to-chat).
     * `queued` / `sent`: reserved for an automated channel, such as the WhatsApp Cloud API.
     */
    status: 'opened' | 'queued' | 'sent'
}

export interface MessageChannel {
    id: string
    label: string
    /** True only when the channel delivers messages without a person pressing send. */
    automated: boolean
    send: (input: { phone: string, message: string, countryCode: string }) => Promise<SendResult>
}

/**
 * Frontend demo channel: opens WhatsApp with the message ready to send.
 * To automate reminders later, add a channel that posts to your backend
 * (for example one that calls the Meta WhatsApp Cloud API) and use it here.
 */
export const whatsAppClickToChat: MessageChannel = {
    id: 'whatsapp-click-to-chat',
    label: 'WhatsApp (click to chat)',
    automated: false,
    send: async ({ phone, message, countryCode }) => {
        window.open(buildWhatsAppLink(phone, message, countryCode), '_blank', 'noopener,noreferrer')

        return { status: 'opened' }
    }
}

export const activeMessageChannel: MessageChannel = whatsAppClickToChat
